import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { EXIT_TYPES, EXIT_TYPE_LABELS, type EmployeeOption, type ExitType, type RecordExitRequest } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { Segmented } from '../../../components/Segmented';
import { Select } from '../../../components/Select';
import { apiGet, errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { usePositions } from '../../organisation/api/organisation-api';
import { useRecordExit } from '../api/requisitions-api';

type Who = 'employee' | 'seat';

/**
 * OFR-06 — F13.17, the manual stand-in for the HRMS's exit event (D2, E20). Someone hired
 * through Nest Recruit is picked by name; anyone from before is a seat and a typed name
 * (S24 Q6). One of six exit types (Q7). The seat shows "becoming vacant" at once and turns
 * vacant on the last working day; a backfill requisition is drafted for someone to submit
 * (F4.12 "requires human submit").
 */
export function RecordExitDialog({ onClose }: { readonly onClose: () => void }): ReactNode {
  const navigate = useNavigate();
  const showToast = useToasts((state) => state.show);
  const record = useRecordExit();
  const [who, setWho] = useState<Who>('employee');
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [positionId, setPositionId] = useState<string | null>(null);
  const [personName, setPersonName] = useState('');
  const [lastWorkingDay, setLastWorkingDay] = useState('');
  const [exitType, setExitType] = useState<ExitType | null>(null);
  const [note, setNote] = useState('');
  const employees = useQuery({ queryKey: ['employees', ''], queryFn: () => apiGet<readonly EmployeeOption[]>('/employees'), enabled: who === 'employee' });
  const positions = usePositions({ page: 1, pageSize: 200 });

  const input = inputOf(who, employeeId, positionId, personName.trim(), lastWorkingDay, exitType, note.trim());
  // Bug #58: someone hired onto a job with no position has no seat to vacate or backfill.
  const isSeatless = who === 'employee' && employees.data?.find((employee) => employee.id === employeeId)?.positionId === null;

  async function submit(): Promise<void> {
    if (!input) return;
    try {
      const result = await record.mutateAsync(input);
      onClose();
      if (!result.hasSeat) {
        showToast({ tone: 'success', message: 'Exit recorded', detail: 'They had no seat on record, so there is nothing to vacate and no backfill to draft.' });
        return;
      }
      const seatLine = result.isSeatEmptied ? 'The seat is vacant now.' : 'The seat turns vacant on the last working day.';
      if (result.requisitionId) {
        showToast({ tone: 'success', message: 'Exit recorded, backfill drafted', detail: `${seatLine} Check the backfill and submit it for approval.` });
        await navigate({ to: '/requisitions/$requisitionId', params: { requisitionId: result.requisitionId } });
        return;
      }
      showToast({ tone: 'info', message: 'Exit recorded', detail: `${seatLine} No backfill drafted: ${result.blockedByReference ?? 'another requisition'} already covers this seat.` });
    } catch (cause) {
      showToast({ tone: 'danger', message: 'The exit was not recorded', detail: errorMessage(cause) });
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Record a leaver"
      description="The seat shows as becoming vacant, turns vacant on their last working day, and a backfill request is drafted for you to check and submit."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="solid" disabled={input === null} isPending={record.isPending} onClick={() => void submit()}>
            {isSeatless ? 'Record the exit' : 'Record and draft backfill'}
          </Button>
        </>
      }
    >
      <Field label="Who is leaving">
        {() => (
          <Segmented
            label="Who is leaving"
            value={who}
            options={[
              { value: 'employee', label: 'Hired through Nest Recruit' },
              { value: 'seat', label: 'Someone from before' },
            ]}
            onChange={setWho}
          />
        )}
      </Field>
      {who === 'employee' ? (
        <Field label="Person" isRequired hint={employees.data?.length === 0 ? `Nobody to choose — no one hired through Nest Recruit who isn't already leaving. Choose "Someone from before".` : undefined}>
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Person"
              placeholder="Choose who is leaving"
              value={employeeId}
              options={(employees.data ?? []).map((employee) => ({
                value: employee.id,
                label: `${employee.fullName} (${employee.employeeCode})`,
                description: [employee.positionCode && employee.positionTitle ? `${employee.positionCode} — ${employee.positionTitle}` : null, employee.departmentName].filter(Boolean).join(', '),
              }))}
              onChange={setEmployeeId}
            />
          )}
        </Field>
      ) : (
        <>
          <Field label="Position" isRequired>
            {({ inputId }) => (
              <Select
                id={inputId}
                label="Position"
                placeholder="Choose the seat they leave"
                value={positionId}
                options={(positions.data?.rows ?? [])
                  .filter((position) => position.archivedAt === null)
                  .map((position) => ({ value: position.id, label: `${position.code} — ${position.title}`, description: `${position.departmentName}, ${position.ventureName}` }))}
                onChange={setPositionId}
              />
            )}
          </Field>
          <Field label="Their name" isRequired>
            {({ inputId }) => <Input id={inputId} maxLength={120} value={personName} placeholder="Rahim Uddin" onChange={(event) => setPersonName(event.target.value)} />}
          </Field>
        </>
      )}
      <Field label="Last working day" isRequired>
        {({ inputId }) => <DatePicker id={inputId} label="Last working day" value={lastWorkingDay} onChange={setLastWorkingDay} />}
      </Field>
      <Field label="Type of exit" isRequired>
        {({ inputId }) => (
          <Select
            id={inputId}
            label="Type of exit"
            placeholder="Choose one"
            value={exitType}
            options={EXIT_TYPES.map((type) => ({ value: type, label: EXIT_TYPE_LABELS[type] }))}
            onChange={(value) => setExitType(EXIT_TYPES.find((type) => type === value) ?? null)}
          />
        )}
      </Field>
      <Field label="Note" isOptional>
        {({ inputId }) => <Textarea id={inputId} rows={2} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />}
      </Field>
    </Modal>
  );
}

/** The request, once everything the chosen kind of exit needs is there; null until then. */
function inputOf(who: Who, employeeId: string | null, positionId: string | null, personName: string, lastWorkingDay: string, exitType: ExitType | null, note: string): RecordExitRequest | null {
  if (!lastWorkingDay || !exitType) return null;
  const extra = note ? { note } : {};
  if (who === 'employee') return employeeId ? { who, employeeId, lastWorkingDay, exitType, ...extra } : null;
  return positionId && personName.length >= 2 ? { who, positionId, personName, lastWorkingDay, exitType, ...extra } : null;
}
